import { type InjectionKey, ref, type ShallowRef } from 'vue'

import patternTextile1Url from '@/assets/img/patterns/pattern_textile_1.png?url'
import patternTextile2Url from '@/assets/img/patterns/pattern_textile_2.png?url'
import patternTextile3Url from '@/assets/img/patterns/pattern_textile_3.png?url'
import patternTextile4Url from '@/assets/img/patterns/pattern_textile_4.png?url'
import patternTextile5Url from '@/assets/img/patterns/pattern_textile_5.png?url'
import patternTextile6Url from '@/assets/img/patterns/pattern_textile_6.png?url'
import type { AssetAtlas } from '@/three/manifest/types'
import type { StageId, StageRoute } from '@/types'

export const menuAlternate = ref(false)
export const introVideoProgress = ref(0)
export const outroVideoProgress = ref(0)
export const dropdownMenuOpen = ref(false)
export const dropdownMenuUnlocked = ref(false)
export const modalLeadFormOpen = ref<boolean>(false)
export const leadFormSubmitSuccess = ref<boolean>(false)
export const isMusicMuted = ref<boolean>(false)
export const subtitlesEnabled = ref<boolean>(true)
export const isAssetsLoaded = ref<boolean>(false)
// Flipped true once the preloader has fully animated out. Use this (not
// isAssetsLoaded) to time post-preloader entrance animations — the preloader
// remains visible for a 2s fake-duration timer plus a 0.62s leave anim after
// isAssetsLoaded becomes true.
export const isPreloaderGone = ref<boolean>(false)

export const PAGE_SECTIONS: Record<StageId, string> = {
  Intro: 'Intro',
  Chapter01: 'Archivio dei tessuti',
  Chapter02: 'Cave di marmo',
  Chapter03: 'Pelletteria di San Miniato',
  Outro: 'Outro',
  Gift: 'Gift'
}

export const DEFAULT_TRANSITION_DURATION = 1000
export const WIPE_FEATHER = 0.1
export const WIPE_END_VALUE = 1.05

export const assetAtlasInjectionKey = Symbol('assetAtlas') as InjectionKey<ShallowRef<AssetAtlas | null>>

export const STAGE_ROUTES: StageRoute[] = [
  { stageId: 'Intro', path: '/intro', label: 'Intro', titleKey: 'stages.intro.title' },
  { stageId: 'Chapter01', path: '/chapter-01', label: 'Chapter 01', titleKey: 'stages.chapter01.title' },
  { stageId: 'Chapter02', path: '/chapter-02', label: 'Chapter 02', titleKey: 'stages.chapter02.title' },
  { stageId: 'Chapter03', path: '/chapter-03', label: 'Chapter 03', titleKey: 'stages.chapter03.title' },
  { stageId: 'Outro', path: '/outro', label: 'Outro', titleKey: 'stages.outro.title' },
  { stageId: 'Gift', path: '/gift', label: 'Gift', titleKey: 'stages.gift.title' }
]

export const CHAPTER_FRAMING: Partial<Record<StageId, { color: string; rotateTexture?: boolean }>> = {
  Chapter01: { color: '#764B41', rotateTexture: true }, // token: bordeaux
  Chapter02: { color: '#594D40', rotateTexture: true }, // token: brown.dark
  Chapter03: { color: '#2F2E25' },                      // token: dark-green
  Gift: { color: '#23280B', rotateTexture: true }        // token: dark.green
}

// Per-pattern frame textures — placeholders, swap with final art when ready
export const CLOTH_ITEMS = [
  { id: '1', texture: patternTextile4Url },
  { id: '2', texture: patternTextile1Url },
  { id: '3', texture: patternTextile5Url },
  { id: '4', texture: patternTextile6Url },
  { id: '5', texture: patternTextile3Url },
  { id: '6', texture: patternTextile2Url }
]

// ─── ChapterTitle ────────────────────────────────────────────────────────────

export interface ChapterTitleWordEntry {
  rotate: number
  rotateMobile: number
}

export interface ChapterTitleConfig {
  color: 'beige' | 'light' | 'grey'
  rows: readonly (readonly ChapterTitleWordEntry[])[]
}

// Rotation values only — text comes from translations via tm()
export const CHAPTER_TITLE_CONFIGS: Record<1 | 2 | 3, ChapterTitleConfig> = {
  1: {
    color: 'beige',
    rows: [
      [
        { rotate: -2.96, rotateMobile: -1.56 },
        { rotate: 0.58, rotateMobile: 1.13 }
      ],
      [
        { rotate: -1.33, rotateMobile: -0.77 },
        { rotate: 0.81, rotateMobile: 1.38 }
      ]
    ]
  },
  2: {
    color: 'light',
    rows: [
      [
        { rotate: -2.96, rotateMobile: -1.56 },
        { rotate: 0.31, rotateMobile: 0.31 },
        { rotate: 4.25, rotateMobile: 4.25 }
      ],
      [
        { rotate: 0.31, rotateMobile: 0.31 }
      ]
    ]
  },
  3: {
    color: 'grey',
    rows: [
      [{ rotate: -6.03, rotateMobile: -6.03 }],
      [{ rotate: -1.91, rotateMobile: -1.91 }],
      [
        { rotate: -0.39, rotateMobile: -0.39 },
        { rotate: 1.90, rotateMobile: 1.90 }
      ],
      [{ rotate: -2.37, rotateMobile: -2.37 }]
    ]
  }
}

export const GIFT_ITEMS = [
  { id: '1', name: 'Pasticcino Bag Italian Roots Large', url: 'https://www.weekendmaxmara.com/p-we5516216406005-italianrootsl?r=0&itm_source=pdp&itm_medium=site&itm_campaign=pasticcino-italian-roots' },
  { id: '2', name: 'Pasticcino Bag Italian Roots Medium', url: 'https://www.weekendmaxmara.com/p-we5516196406001-italianrootsm?r=0&itm_source=pdp&itm_medium=site&itm_campaign=pasticcino-italian-roots' },
  { id: '3', name: 'Pasticcino Bag Italian Roots Small', url: 'https://it.weekendmaxmara.com/p-we5516186406002-italianrootss-rosa?r=0&itm_source=pdp&itm_medium=site&itm_campaign=pasticcino-italian-roots' },
  { id: '4', name: 'Pasticcino Bag Italian Roots Small', url: 'https://www.weekendmaxmara.com/p-we5516186406004-italianrootss?r=0&itm_source=pdp&itm_medium=site&itm_campaign=pasticcino-italian-roots' },
  { id: '5', name: 'Pasticcino Bag Italian Roots Small', url: 'https://www.weekendmaxmara.com/p-we5516206406006-italianrootss1?r=0&itm_source=pdp&itm_medium=site&itm_campaign=pasticcino-italian-roots' },
  { id: '6', name: 'Pasticcino Bag Italian Roots Small', url: 'https://www.weekendmaxmara.com/p-we5516186406003-italianrootss?r=0&itm_source=pdp&itm_medium=site&itm_campaign=pasticcino-italian-roots' }
]

// ITU-T E.164 country calling codes — Italy first, then Europe, Americas, Asia-Pacific, Middle East & Africa
export const PHONE_PREFIXES: { value: string; label: string }[] = [
  // Italy first — default
  { value: '+39', label: '+39 IT' },
  // Europe
  { value: '+43', label: '+43 AT' },
  { value: '+32', label: '+32 BE' },
  { value: '+387', label: '+387 BA' },
  { value: '+359', label: '+359 BG' },
  { value: '+385', label: '+385 HR' },
  { value: '+357', label: '+357 CY' },
  { value: '+420', label: '+420 CZ' },
  { value: '+45', label: '+45 DK' },
  { value: '+372', label: '+372 EE' },
  { value: '+358', label: '+358 FI' },
  { value: '+33', label: '+33 FR' },
  { value: '+49', label: '+49 DE' },
  { value: '+30', label: '+30 GR' },
  { value: '+36', label: '+36 HU' },
  { value: '+354', label: '+354 IS' },
  { value: '+353', label: '+353 IE' },
  { value: '+371', label: '+371 LV' },
  { value: '+423', label: '+423 LI' },
  { value: '+370', label: '+370 LT' },
  { value: '+352', label: '+352 LU' },
  { value: '+356', label: '+356 MT' },
  { value: '+373', label: '+373 MD' },
  { value: '+377', label: '+377 MC' },
  { value: '+382', label: '+382 ME' },
  { value: '+31', label: '+31 NL' },
  { value: '+47', label: '+47 NO' },
  { value: '+48', label: '+48 PL' },
  { value: '+351', label: '+351 PT' },
  { value: '+40', label: '+40 RO' },
  { value: '+7', label: '+7 RU' },
  { value: '+381', label: '+381 RS' },
  { value: '+421', label: '+421 SK' },
  { value: '+386', label: '+386 SI' },
  { value: '+34', label: '+34 ES' },
  { value: '+46', label: '+46 SE' },
  { value: '+41', label: '+41 CH' },
  { value: '+380', label: '+380 UA' },
  { value: '+44', label: '+44 GB' },
  { value: '+379', label: '+379 VA' },
  // Americas
  { value: '+1', label: '+1 US/CA' },
  { value: '+54', label: '+54 AR' },
  { value: '+55', label: '+55 BR' },
  { value: '+56', label: '+56 CL' },
  { value: '+57', label: '+57 CO' },
  { value: '+52', label: '+52 MX' },
  { value: '+51', label: '+51 PE' },
  { value: '+58', label: '+58 VE' },
  // Asia-Pacific
  { value: '+61', label: '+61 AU' },
  { value: '+86', label: '+86 CN' },
  { value: '+852', label: '+852 HK' },
  { value: '+91', label: '+91 IN' },
  { value: '+62', label: '+62 ID' },
  { value: '+81', label: '+81 JP' },
  { value: '+60', label: '+60 MY' },
  { value: '+64', label: '+64 NZ' },
  { value: '+63', label: '+63 PH' },
  { value: '+65', label: '+65 SG' },
  { value: '+82', label: '+82 KR' },
  { value: '+886', label: '+886 TW' },
  { value: '+66', label: '+66 TH' },
  { value: '+84', label: '+84 VN' },
  // Middle East & Africa
  { value: '+971', label: '+971 AE' },
  { value: '+973', label: '+973 BH' },
  { value: '+20', label: '+20 EG' },
  { value: '+972', label: '+972 IL' },
  { value: '+962', label: '+962 JO' },
  { value: '+965', label: '+965 KW' },
  { value: '+961', label: '+961 LB' },
  { value: '+212', label: '+212 MA' },
  { value: '+968', label: '+968 OM' },
  { value: '+974', label: '+974 QA' },
  { value: '+966', label: '+966 SA' },
  { value: '+27', label: '+27 ZA' },
  { value: '+216', label: '+216 TN' },
  { value: '+90', label: '+90 TR' }
]
